import { useEffect, useMemo, useState } from 'react';
import { usePlotsStore } from '@/store/plots';
import { authorizedFetch } from '@/lib/queryClient';
import { useProjectsStore } from '@/store/projects';

export type CodeLanguage = 'python' | 'javascript';

interface UsePlotlyCodeResult {
  language: CodeLanguage;
  setLanguage: (lang: CodeLanguage) => void;
  code: string;
  copyToClipboard: () => Promise<void>;
  download: () => void;
  downloadEdited: () => void;
}

export function usePlotlyCode(defaultLanguage: CodeLanguage = 'python'): UsePlotlyCodeResult {
  const { currentPlot, setCurrentPlotArtifacts } = usePlotsStore();
  const { currentProject } = useProjectsStore();
  const [language, setLanguage] = useState<CodeLanguage>(defaultLanguage);
  const [serverCode, setServerCode] = useState<string>('');

  // Always prefer the exact python that the backend executed, if available
  useEffect(() => {
    let cancelled = false;
    async function fetchServerCode() {
      try {
        const plotId = (currentPlot as any)?.id;
        const plotlyJson = (currentPlot as any)?.plotlyJson;
        const existingPythonCode = (currentPlot as any)?.pythonCode;
        const artifactSource = (currentPlot as any)?.artifactSource as ('draft'|'version'|undefined);
        const latestVersionId = (currentPlot as any)?.latestVersionId as (string|undefined);

        // If the plot already has codegen output, use it immediately
        if (existingPythonCode && typeof existingPythonCode === 'string' && existingPythonCode.length > 0) {
          if (!cancelled) setServerCode(existingPythonCode);
          return;
        }

        // If we are in a fresh new-plot state (no plotId yet), do not fetch
        if (!plotId) {
          setServerCode('');
          return;
        }
        // Branch by source: prefer draft resolution for fresh runs; versions for saved
        setServerCode('');
        const isDraft = (artifactSource === 'draft') || !latestVersionId;
        const pid = currentProject?.id || (window.location.pathname.match(/plot-editor\/(.+?)(?:\/|$)/)?.[1]);
        if (isDraft) {
          // Fresh run: export-code against draft artifacts with project scoping
          try {
            const res = await authorizedFetch(`/api/ai/v2/export-code?projectId=${encodeURIComponent(String(pid || ''))}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ plotId, format: 'python', currentPlotlyJson: plotlyJson, claimToken: ((): string | null => { try { return (window as any).__lumiplotClaims?.[String(plotId)] || null; } catch { return null; } })() })
            });
            const data = await res.json();
            const code = String(data?.code || '');
            if (!cancelled && code) {
              setServerCode(code);
              try { setCurrentPlotArtifacts({ id: plotId, pythonCode: code, artifactSource: 'draft' } as any); } catch {}
            }
          } catch {
            // Attempt direct draft script if export-code failed
            // Store draft fallback under the active plot id so consumers receive code even after id switches to saved
            const draftId = String((currentPlot as any)?.draftId || plotId || '');
            if (pid && draftId) {
              try {
                const resp = await authorizedFetch(`/api/ai/v2/drafts/${pid}/${draftId}/edited_script.py`);
                const txt = await resp.text();
                if (!cancelled && txt) {
                  setServerCode(txt);
                  try { setCurrentPlotArtifacts({ id: plotId, pythonCode: txt, artifactSource: 'draft' } as any); } catch {}
                }
              } catch {}
            }
          }
          return;
        }

        // Saved plot: try edited_script, then script, then export-code as last resort
        try {
          let txt: string | null = null;
          try {
            const r = await authorizedFetch(`/api/ai/v2/plots/${plotId}/versions/latest/edited_script.py`);
            txt = await r.text();
          } catch {
            try {
              const r2 = await authorizedFetch(`/api/ai/v2/plots/${plotId}/versions/latest/script.py`);
              txt = await r2.text();
            } catch {
              // If latest scripts unavailable, attempt draft fallback for the bound draft only
              try {
                const parts = window.location.pathname.split('/');
                const boundDraftId = parts[3] || null;
                if (pid && boundDraftId) {
                  try {
                    const d = await authorizedFetch(`/api/ai/v2/drafts/${pid}/${boundDraftId}/edited_script.py`);
                    txt = await d.text();
                  } catch {}
                }
              } catch {}
            }
          }
          if (!cancelled && typeof txt === 'string' && txt.trim().length > 0) {
            setServerCode(txt);
            try { setCurrentPlotArtifacts({ id: plotId, pythonCode: txt, artifactSource: 'version' } as any); } catch {}
            return;
          }
        } catch {}

        // Last resort: export-code using saved figure
        try {
          const res = await authorizedFetch(`/api/ai/v2/export-code?projectId=${encodeURIComponent(String(pid || ''))}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ plotId, format: 'python', currentPlotlyJson: plotlyJson, claimToken: ((): string | null => { try { return (window as any).__lumiplotClaims?.[String(plotId)] || null; } catch { return null; } })() })
          });
          const data = await res.json();
          const code = String(data?.code || '');
          if (!cancelled && code) {
            setServerCode(code);
            try { setCurrentPlotArtifacts({ id: plotId, pythonCode: code, artifactSource: 'version' } as any); } catch {}
          }
        } catch {}
      } catch {}
    }
    fetchServerCode();
    return () => { cancelled = true; };
  }, [ (currentPlot as any)?.id, (currentPlot as any)?.plotlyJson, (currentPlot as any)?.pythonCode ]);

  const { data, layout } = useMemo(() => {
    const pj: any = currentPlot?.plotlyJson || {};
    return {
      data: Array.isArray(pj?.data) ? pj.data : [],
      layout: pj?.layout || {}
    };
  }, [currentPlot?.plotlyJson]);


  const jsCode = useMemo(() => {
    try {
      const safeData = JSON.stringify(data ?? [], null, 2);
      const safeLayout = JSON.stringify(layout ?? {}, null, 2);
      const code = [
        "import Plotly from 'plotly.js-dist-min'",
        '',
        'const data = ' + safeData,
        'const layout = ' + safeLayout,
        "Plotly.newPlot('plot', data, layout)",
      ].join('\n');
      
      // Log if data is empty (for debugging)
      if (!data || data.length === 0) {
        try { console.warn('[usePlotlyCode] Generating JavaScript code with empty data array', { plotId: (currentPlot as any)?.id }); } catch {}
      }
      
      return code;
    } catch (error) {
      try { console.error('[usePlotlyCode] Error generating JavaScript code:', error); } catch {}
      return "// Error generating JavaScript code";
    }
  }, [data, layout, currentPlot]);

  const code = useMemo(() => {
    // Python tab: show only backend-provided CodeGen script; no local px fallback
    if (language === 'python') {
      return (serverCode && serverCode.length > 0)
        ? serverCode
        : '# Python code will appear once the plot is generated (waiting for CodeGen).';
    }
    // JavaScript tab: always use live frontend-generated code
    return jsCode;
  }, [language, serverCode, jsCode]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {}
  };

  const download = () => {
    if (language === 'python') {
      const plotId = (currentPlot as any)?.id;
      if (!plotId) return;
      (async () => {
        try {
          // Prefer direct stored script to avoid navigation/route guards
          let res = await authorizedFetch(`/api/ai/v2/plots/${plotId}/versions/latest/script.py`);
          if (!res.ok) res = await authorizedFetch(`/api/ai/v2/plots/${plotId}/script.py`);
          if (!res.ok) return;
          const txt = await res.text();
          const blob = new Blob([txt], { type: 'text/x-python' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `plot_${plotId}.py`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } catch {}
      })();
      return;
    }
    // JavaScript: client-side download of generated code
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plot.js';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadEdited = () => {
    const plotId = (currentPlot as any)?.id;
    const plotlyJson = (currentPlot as any)?.plotlyJson;
    if (!plotId || !plotlyJson) return;
    (async () => {
      try {
        // Always build edited script against current editor state so patch is guaranteed
        const post = await authorizedFetch(`/api/ai/v2/plots/${plotId}/script-diff-only.py`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ plotId, currentPlotlyJson: plotlyJson })
        });
        let txt: string;
        if (post.ok) {
          txt = await post.text();
        } else {
          // Fallback to stored edited script (may be original if no edits yet)
          const res = await authorizedFetch(`/api/ai/v2/plots/${plotId}/versions/latest/edited_script.py`);
          if (!res.ok) return;
          txt = await res.text();
        }
        const blob = new Blob([txt], { type: 'text/x-python' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `plot_${plotId}_edited.py`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch {}
    })();
  };

  return { language, setLanguage, code, copyToClipboard, download, downloadEdited };
}

// Removed local code generation helpers; Python is sourced from server or export-code
